import * as React from "react";

import {
  InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland,
  InputContainer,
  InputContainerProps,
} from "../form";

interface ReCaptchaProps extends InputContainerProps {
  recaptchaConfiguration: InitialStateMinimumRequirements_Recaptcha_ForAnyParticularConfigurationIsland;
  onChange: (name: string, value: boolean) => void;
}

const setupCaptcha = (
  recaptchaId: any,
  jsLayout: any,
  onCaptchaValidationStateChange: React.RefObject<
    (value: boolean) => void | null
  >
) => {
  (window.require as any)(
    [
      "jquery",
      "underscore",
      "Magento_ReCaptchaFrontendUi/js/reCaptchaScriptLoader",
      "Magento_ReCaptchaFrontendUi/js/nonInlineReCaptchaRenderer",
    ],
    function (
      $: any,
      _: any,
      reCaptchaLoader: any,
    ) {
      let _isApiRegistered: any;
      let captchaInitialized: any;

      const getReCaptchaId = () => {
        return recaptchaId;
      };
      const recaptchaApiReadyEventName =
        "recaptchaapiready." +
        String(getReCaptchaId()).replace(/[^A-Za-z0-9]/g, "");

      const _loadApi = function () {
        if (_isApiRegistered !== undefined) {
          if (_isApiRegistered === true) {
            $(window).trigger("recaptchaapiready");
          }

          return;
        }
        _isApiRegistered = false;

        // global function
        (window as any).globalOnRecaptchaOnLoadCallback = function () {
          _isApiRegistered = true;
          $(window).trigger("recaptchaapiready");
        };

        reCaptchaLoader.addReCaptchaScriptTag();
      };

      const reCaptchaCallback = function (token: any) { console.log(token)};

      const validateReCaptcha = function (state: any) {
        const returnVal = $(document)
          .find("input[type=checkbox].required-captcha")
          .prop("checked", state);

        if (onCaptchaValidationStateChange?.current) {
          onCaptchaValidationStateChange.current(state);
        }

        return returnVal;
      };

      const initCaptcha = function () {
        var $wrapper, $reCaptcha, parameters;

        if (captchaInitialized) {
          return;
        }

        $wrapper = $("#" + getReCaptchaId() + "-react-wrapper");
        $reCaptcha = $wrapper.find(".g-recaptcha");
        if ($wrapper.length === 0 || $reCaptcha.length === 0) {
          $(window).off(recaptchaApiReadyEventName);
          return;
        }
        $(window).off(recaptchaApiReadyEventName);

        captchaInitialized = true;

        /*
         * Workaround for data-bind issue:
         * We cannot use data-bind to link a dynamic id to our component
         * See:
         * https://stackoverflow.com/questions/46657573/recaptcha-the-bind-parameter-must-be-an-element-or-id
         *
         * We create a wrapper element with a wrapping id and we inject the real ID with jQuery.
         * In this way we have no data-bind attribute at all in our reCAPTCHA div
         */
        $reCaptcha.attr("id", getReCaptchaId());

        $wrapper.parents("form");

        parameters = _.extend(
          {
            callback: function (token: any) {
              // jscs:ignore jsDoc
              reCaptchaCallback(token);
              validateReCaptcha(true);
            },
            "expired-callback": function () {
              validateReCaptcha(false);
            },
          },
          jsLayout?.components[recaptchaId]?.settings?.rendering
        );

        // eslint-disable-next-line no-undef
        try {
          (window as any).grecaptcha.render(
            getReCaptchaId(),
            parameters
          );
        } catch (error) {
          if (
            `${error}` !==
            "Error: reCAPTCHA has already been rendered in this element"
          ) {
            throw error;
          }
        }
      };

      const renderReCaptcha = function () {
        if ((window as any).grecaptcha && (window as any).grecaptcha.render) {
          // Check if reCAPTCHA is already loaded
          initCaptcha();
        } else {
          // Wait for reCAPTCHA to be loaded
          $(window).off(recaptchaApiReadyEventName);
          $(window).one(recaptchaApiReadyEventName, function () {
            initCaptcha();
          });
        }
      };

      _loadApi();
      renderReCaptcha();
    }
  );
};

export const ReCaptcha = (
  props: ReCaptchaProps
): React.ReactElement<any, any> | null => {
  const { recaptchaId, recaptchaJsLayout, recaptchaEnabled } =
    props.recaptchaConfiguration || {};

  const captchaRef = React.useRef<HTMLDivElement | null>(null);
  const onChangeRef = React.useRef<((value: boolean) => void) | null>(null);
  const isRecaptchaV3 = recaptchaEnabled as unknown as string === 'reCAPTCHA v3 Invisible';

  /**
   * Once a captcha is rendered, you can't rerender on top of it,
   * it's not good to destroy and rerender either because react will rerender it many times easily,
   * so we keep a ref that we bind to the captcha callback and update that ref as we go.
   */
  React.useLayoutEffect(() => {
    onChangeRef.current = (value: boolean) => {
      props.onChange(props.name, value);
    };
  }, [props.form, props.name, props.onChange]);

  const mountRecaptcha = (element: HTMLDivElement) => {
    if (captchaRef.current && element && captchaRef.current === element) {
      return;
    }
    captchaRef.current = element;
    if (element) {
      (window.require as any)(["jquery"], function (jquery: any) {
        const $element = jquery(element);
        if ($element && $element.length > 0) {
          $element.trigger("contentUpdated");

          setupCaptcha(recaptchaId, recaptchaJsLayout, onChangeRef);
        }
      });
    }
  };

  if (!recaptchaEnabled) {
    return null;
  }

  React.useEffect(() => {
    // Script for recaptcha v3
    if (isRecaptchaV3) {
      const siteKey =
          recaptchaJsLayout?.components[recaptchaId]?.settings?.rendering.sitekey;

      (window as any).recaptchaSiteKey = siteKey;

      if (siteKey) {
        const script = document.createElement("script");
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;

        document.head.appendChild(script);
      }
    }
  }, []);

  return (
    <InputContainer
      {...props}
      className={isRecaptchaV3 ? "display-none" : "recaptcha-input"}
    >
      <div className="recaptcha-template-container" ref={mountRecaptcha}>
        <div className="field-recaptcha" id={`${recaptchaId}-container`}>
          <div id={`${recaptchaId}-react-wrapper`}>
            <div className="g-recaptcha"/>
            <div className="field">
              <div className="control">
                <input
                  type="checkbox"
                  value=""
                  className="required-captcha checkbox"
                  name="recaptcha-validate-"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </InputContainer>
  );
};
